// One stage's errors over some games (the middlegame's, the ending's), and
// the rule behind the library's `blunder` filter. The building block of the
// middlegame and ending cards: their counts are the filter's games.
import { type GameBlunder } from '@chess/api-model';
import { aggregatorId, type Accumulator, type Aggregator, type AggregatorId } from '@chess/api-model';
import { type StageErrorSummary } from './types.ts';
import { LOST_PIECES, type GameFacts, type LostPiece } from '@chess/api-model';

/** The stages with error facts: `endgame` is the ending (named so as not to clash with how the game ended). */
export type ErrorStage = 'middlegame' | 'endgame';

const pieces = () => LOST_PIECES.reduce((all, p) => ({ ...all, [p]: 0 }), {} as Record<LostPiece, number>);

/** A stage's errors with nothing counted. */
export const emptyStageErrors = (): StageErrorSummary => ({
  scanned: 0, blunders: 0, blunderGames: 0, misses: 0, missGames: 0, lost: pieces(), lostGames: pieces(), lostAnyGames: 0, thrown: 0, thrownGames: 0, winning: 0,
});

/** One stage's errors: scanned games that reached that stage only. */
export class StageErrorsAccumulator implements Accumulator<StageErrorSummary> {
  private readonly summary = emptyStageErrors();
  private readonly stage: ErrorStage;

  constructor(stage: ErrorStage) {
    this.stage = stage;
  }

  add(g: GameFacts): void {
    const m = g.scanned ? g[this.stage] : undefined;
    if (!m) return;
    const s = this.summary;
    s.scanned += 1;
    s.blunders += m.blunders;
    s.misses += m.misses;
    s.thrown += m.thrown;
    if (m.blunders) s.blunderGames += 1;
    if (m.misses) s.missGames += 1;
    if (m.thrown) s.thrownGames += 1;
    if (m.winning) s.winning += 1;
    for (const p of m.lost) s.lost[p] += 1;
    for (const p of new Set(m.lost)) s.lostGames[p] += 1;
    if (m.lost.some((p) => p !== 'mate')) s.lostAnyGames += 1;
  }

  result(): StageErrorSummary {
    const s = this.summary;
    return { ...s, lost: { ...s.lost }, lostGames: { ...s.lostGames } };
  }
}

/**
 * One stage's errors as an aggregator, shared by every card of that stage:
 * each card registers it, the service runs it once (same id) and keeps it
 * until the last card unregisters.
 */
export class StageErrorsAggregator implements Aggregator<StageErrorSummary> {
  readonly id: AggregatorId;
  private readonly stage: ErrorStage;

  constructor(stage: ErrorStage) {
    this.stage = stage;
    this.id = aggregatorId(`stage-errors:${stage}`);
  }

  start(): Accumulator<StageErrorSummary> {
    return new StageErrorsAccumulator(this.stage);
  }
}

/** One stage's errors over some games. */
export function stageSummary(games: Iterable<GameFacts>, stage: ErrorStage): StageErrorSummary {
  const acc = new StageErrorsAccumulator(stage);
  for (const g of games) acc.add(g);
  return acc.result();
}

/** Middlegame errors over some games. */
export function middlegameSummary(games: Iterable<GameFacts>): StageErrorSummary {
  return stageSummary(games, 'middlegame');
}

/** Whether a game has this kind of blunder: the rule behind the library's `blunder` filter. */
export function hasBlunder(game: GameFacts, kind: GameBlunder): boolean {
  if (!game.scanned) return false;
  const m = game.middlegame;
  const e = game.endgame;
  switch (kind) {
    case 'ending':
      return (game.endingBlunders?.length ?? 0) > 0;
    case 'ending-blunder':
      return (e?.blunders ?? 0) > 0;
    case 'ending-miss':
      return (e?.misses ?? 0) > 0;
    case 'ending-won':
      return (e?.thrown ?? 0) > 0;
    case 'middlegame':
      return (m?.blunders ?? 0) > 0;
    case 'middlegame-miss':
      return (m?.misses ?? 0) > 0;
    case 'middlegame-won':
      return (m?.thrown ?? 0) > 0;
    case 'ending-time':
      return game.endingCase === 'time';
    case 'ending-slipped':
      return game.endingCase === 'slipped';
    case 'ending-missed':
      return game.endingCase === 'missed';
    case 'ending-lost':
      return game.endingCase === 'lost';
    default:
      if (kind.startsWith('ending-')) return !!e?.lost.includes(kind.slice('ending-'.length) as LostPiece);
      return !!m?.lost.includes(kind.slice('middlegame-'.length) as LostPiece);
  }
}
